// W3-I garments costing. Money fields arrive as JSON numbers.

export interface CostingSettings {
    institutionId: string;
    enabled: boolean;
    wipAccountId?: string | null;
    fgAccountId?: string | null;
    cogsAccountId?: string | null;
    absorbedAccountId?: string | null;
    writeOffAccountId?: string | null;
    absorptionDriver: 'OUTPUT_QTY' | 'PLANNED_CM_VALUE';
    overshipTolerancePct: number;
    poolAccountIds: string[];
}
export type CostingSettingsRequest = Omit<CostingSettings, 'institutionId'>;

export interface SuggestedAccountsResult {
    created: string[]; present: string[]; skipped: string[]; settings: CostingSettings; missingRoles?: string[];
}

export type OutputStage = 'CUT' | 'SEWN' | 'FINISHED';
export const OUTPUT_STAGES: OutputStage[] = ['CUT', 'SEWN', 'FINISHED'];

export interface ProductionOutput {
    id: string; buyerOrderId: string; orderNumber: string; buyerOrderLineId?: string | null;
    outputDate: string; stage: OutputStage; quantity: number; notes?: string | null; createdAt?: string;
}
export interface ProductionOutputRequest {
    buyerOrderId: string; buyerOrderLineId?: string | null; outputDate: string; stage: OutputStage; quantity: number; notes?: string | null;
}

export interface AbsorptionRunLine {
    id: string; kind: string; segmentId?: string | null; buyerOrderId?: string | null; orderNumber?: string | null;
    driverValue?: number | null; amount: number; journalEntryId?: string | null; reversalJournalEntryId?: string | null; error?: string | null;
}
export interface AbsorptionRun {
    id: string; periodStart: string; periodEnd: string; status: 'DRAFT' | 'POSTED' | 'REVERSED'; driver: string;
    poolTotal: number; absorbedTotal: number; notAbsorbedTotal: number; postedAt?: string | null; reversedAt?: string | null;
    lines: AbsorptionRunLine[];
}

export interface CostEvent {
    id: string; buyerOrderId: string; orderNumber: string; kind: 'WIP_TO_FG' | 'SHIPMENT_COGS' | 'ORDER_COST_CLOSE';
    status: 'POSTED' | 'REVERSED'; eventDate: string; quantity: number; value: number; exportInvoiceId?: string | null;
    journalEntryId?: string | null; reversalJournalEntryId?: string | null; notes?: string | null; dateNote?: string | null;
}
export interface PendingShipment {
    exportInvoiceId: string; invoiceNumber: string; invoiceDate: string; buyerOrderId: string; orderNumber?: string | null;
    currencyCode?: string | null; quantity?: number | null; amount?: number | null; fgQuantityOnHand: number;
    /** Server-computed: default COGS date, earliest allowed date (last costing event / absorbed month), latest (today, Dhaka). */
    suggestedDate?: string | null; minDate?: string | null; maxDate?: string | null;
}

export interface OrderCostRow {
    orderId: string; orderNumber: string; buyerId?: string | null; status: string; shipDate?: string | null;
    currencyCode?: string | null; orderQuantity?: number | null; revenue: number; cogs: number; taggedExpense: number;
    wipBalance: number; fgBalance: number; margin: number; marginPct?: number | null; closedWithResidual: boolean;
    commission?: number; costIncomplete?: boolean;
}
export interface ShipmentRevenue {
    exportInvoiceId: string; invoiceNumber: string; invoiceDate: string; currencyCode?: string | null; amount: number;
    rate?: number | null; revenue: number; costed: boolean;
}
export interface PlanFigures {
    currencyCode?: string | null; exchangeRate?: number | null; revenue?: number | null; costBasis?: number | null;
    costBasisLabel?: string | null; margin?: number | null; marginPct?: number | null;
}
export interface OrderCostDetail {
    row: OrderCostRow; cutQuantity: number; sewnQuantity: number; finishedQuantity: number; transferredQuantity: number;
    shippedQuantity: number; fgQuantityOnHand: number; events: CostEvent[]; shipments: ShipmentRevenue[];
    plan?: PlanFigures | null; notes: string[]; earliestEventDate?: string | null;
}
export interface OrderCostPage { content: OrderCostRow[]; totalElements: number; }
